import { type FormEvent, useEffect, useState } from "react";
import {
  ApiError,
  api,
  type Asesor,
  type AsesorCliente,
} from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";

/**
 * Tab "Asesores comerciales" de /configuracion. Solo admin (require_admin en
 * backend; el tab lo muestra solo al admin).
 *
 * UX: lista de asesores. Click expand → datos editables inline + cartera
 * de clientes (GET /api/asesores/{id}/clientes).
 * Toggle "activo" guarda al instante (PATCH propio + toast).
 * Eliminar deshabilitado si clientes_count > 0 (el backend devuelve 409).
 */
export default function AsesoresPanel() {
  const toast = useToast();
  const [asesores, setAsesores] = useState<Asesor[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [confirmDel, setConfirmDel] = useState<Asesor | null>(null);

  // Formulario de alta
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [emailNuevo, setEmailNuevo] = useState("");
  const [telNuevo, setTelNuevo] = useState("");
  const [notasNuevo, setNotasNuevo] = useState("");
  const [creando, setCreando] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const list = await api.get<Asesor[]>("/api/asesores?incluir_inactivos=true");
      setAsesores(list);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar asesores", msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function crearAsesor(e: FormEvent) {
    e.preventDefault();
    if (!nombreNuevo.trim()) return;
    setCreando(true);
    try {
      const a = await api.post<Asesor>("/api/asesores", {
        nombre: nombreNuevo.trim(),
        email: emailNuevo.trim() || null,
        telefono: telNuevo.trim() || null,
        notas: notasNuevo.trim() || null,
      });
      toast.success("Asesor creado", a.nombre);
      setNombreNuevo("");
      setEmailNuevo("");
      setTelNuevo("");
      setNotasNuevo("");
      // Auto-expandir para que el usuario vea los datos sin otro clic
      setExpanded((prev) => new Set(prev).add(a.id));
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(a: Asesor) {
    try {
      await api.patch(`/api/asesores/${a.id}`, { activo: !a.activo });
      toast.success(a.activo ? "Asesor desactivado" : "Asesor reactivado", a.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  async function eliminarAsesor(a: Asesor) {
    try {
      await api.del(`/api/asesores/${a.id}`);
      toast.success("Asesor eliminado", a.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  return (
    <>
      {/* ── Alta de asesor ── */}
      <form onSubmit={crearAsesor} className="card space-y-3">
        <label className="text-xs uppercase tracking-wide text-zinc-400">
          Nuevo asesor comercial
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text"
            className="input"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="Nombre completo (requerido)"
            required
          />
          <input
            type="email"
            className="input"
            value={emailNuevo}
            onChange={(e) => setEmailNuevo(e.target.value)}
            placeholder="correo@empresa.com (opcional)"
          />
          <input
            type="text"
            className="input"
            value={telNuevo}
            onChange={(e) => setTelNuevo(e.target.value)}
            placeholder="Teléfono (opcional)"
          />
          <input
            type="text"
            className="input"
            value={notasNuevo}
            onChange={(e) => setNotasNuevo(e.target.value)}
            placeholder="Notas (opcional)"
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!nombreNuevo.trim() || creando}
            className="btn-primary disabled:opacity-50"
          >
            {creando ? "Creando..." : "+ Agregar"}
          </button>
        </div>
      </form>

      {/* ── Lista ── */}
      <div className="card">
        {loading ? (
          <SkeletonRows rows={4} cols={3} />
        ) : asesores.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No hay asesores todavía. Agrega el primero arriba.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {asesores.map((a) => {
              const isExpanded = expanded.has(a.id);
              const tieneClientes = a.clientes_count > 0;
              return (
                <li key={a.id} className={a.activo ? "" : "opacity-50"}>
                  {/* ── Fila principal ── */}
                  <div className="py-2 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    {/* Nombre + chip clientes */}
                    <button
                      type="button"
                      onClick={() => toggleExpand(a.id)}
                      className="flex-1 text-left flex items-center gap-2 hover:text-white"
                    >
                      <span className="text-zinc-400 text-xs w-3">
                        {isExpanded ? "▼" : "▶"}
                      </span>
                      <span className="text-zinc-200">{a.nombre}</span>
                      <span
                        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                          tieneClientes
                            ? "border-accent-500/40 bg-accent-500/10 text-accent-300"
                            : "border-ink-600 bg-ink-800 text-zinc-500"
                        }`}
                      >
                        {a.clientes_count} {a.clientes_count === 1 ? "cliente" : "clientes"}
                      </span>
                    </button>

                    {/* Acciones */}
                    <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0">
                      {/* Toggle activo — guarda al instante */}
                      <Toggle
                        checked={a.activo}
                        onChange={() => { void toggleActivo(a); }}
                        label={a.activo ? "Activo" : "Inactivo"}
                        className="text-xs"
                      />

                      {/* Eliminar */}
                      {tieneClientes ? (
                        <span className="btn-ghost text-xs text-red-400/40 cursor-not-allowed select-none">
                          Eliminar
                        </span>
                      ) : (
                        <button
                          onClick={() => setConfirmDel(a)}
                          className="btn-ghost text-xs text-red-400 hover:text-red-300"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ── Detalle expandido ── */}
                  {isExpanded && (
                    <DetalleAsesor asesor={a} onChanged={refresh} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar "${confirmDel.nombre}"`}
          description="No hay clientes asignados. Una vez eliminado no se puede recuperar."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminarAsesor(confirmDel)}
        />
      )}
    </>
  );
}

// ============================================================================
// Sub-panel de detalle y edición del asesor (inline, expandido)
// ============================================================================

function DetalleAsesor({
  asesor,
  onChanged,
}: {
  asesor: Asesor;
  onChanged: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cartera, setCartera] = useState<AsesorCliente[] | null>(null);
  const [loadingCartera, setLoadingCartera] = useState(false);

  // Campos del formulario de edición
  const [nombre, setNombre] = useState(asesor.nombre);
  const [email, setEmail] = useState(asesor.email ?? "");
  const [telefono, setTelefono] = useState(asesor.telefono ?? "");
  const [notas, setNotas] = useState(asesor.notas ?? "");

  // Cargar cartera al montar
  useEffect(() => {
    setLoadingCartera(true);
    api
      .get<AsesorCliente[]>(`/api/asesores/${asesor.id}/clientes`)
      .then((list) => setCartera(list))
      .catch(() => setCartera([]))
      .finally(() => setLoadingCartera(false));
  }, [asesor.id]);

  async function guardarEdicion(e: FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) return;
    setGuardando(true);
    try {
      await api.patch(`/api/asesores/${asesor.id}`, {
        nombre: nombre.trim(),
        email: email.trim() || null,
        telefono: telefono.trim() || null,
        notas: notas.trim() || null,
      });
      toast.success("Asesor actualizado");
      setEditando(false);
      await onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo guardar", msg);
    } finally {
      setGuardando(false);
    }
  }

  function cancelarEdicion() {
    setNombre(asesor.nombre);
    setEmail(asesor.email ?? "");
    setTelefono(asesor.telefono ?? "");
    setNotas(asesor.notas ?? "");
    setEditando(false);
  }

  return (
    <div className="ml-5 mb-3 border-l-2 border-ink-700 pl-3 space-y-3">
      {editando ? (
        /* ── Formulario de edición ── */
        <form onSubmit={guardarEdicion} className="space-y-2 bg-ink-900 rounded p-2 border border-ink-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              autoFocus
              className="input"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre completo"
              required
            />
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@empresa.com"
            />
            <input
              type="text"
              className="input"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Teléfono"
            />
            <input
              type="text"
              className="input"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Notas"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={guardando || !nombre.trim()}
              className="btn-primary text-xs disabled:opacity-50"
            >
              {guardando ? "Guardando..." : "Guardar cambios"}
            </button>
            <button type="button" onClick={cancelarEdicion} className="btn-ghost text-xs">
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        /* ── Vista de datos ── */
        <div className="space-y-1">
          {(asesor.email || asesor.telefono) && (
            <p className="text-xs text-zinc-400">
              {asesor.email && <span>{asesor.email}</span>}
              {asesor.email && asesor.telefono && <span className="mx-1">·</span>}
              {asesor.telefono && <span>{asesor.telefono}</span>}
            </p>
          )}
          {asesor.notas && (
            <p className="text-xs text-zinc-500 italic">{asesor.notas}</p>
          )}
          {!asesor.email && !asesor.telefono && !asesor.notas && (
            <p className="text-xs text-zinc-600">Sin datos de contacto.</p>
          )}
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="btn-ghost text-xs text-accent-400 hover:text-accent-300 mt-1"
          >
            Editar datos
          </button>
        </div>
      )}

      {/* ── Cartera de clientes ── */}
      <div>
        <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
          Cartera de clientes
        </p>
        {loadingCartera ? (
          <p className="text-xs text-zinc-500">Cargando...</p>
        ) : cartera === null || cartera.length === 0 ? (
          <p className="text-xs text-zinc-600">Sin clientes asignados.</p>
        ) : (
          <ul className="space-y-0.5">
            {cartera.map((c) => (
              <li key={c.id} className="text-xs text-zinc-300 flex items-center gap-1">
                <span className="text-zinc-600">·</span>
                {c.nombre}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
